/**
 * External dependencies
 */
import React from 'react';
import { __, sprintf } from '@wordpress/i18n';
import { createInterpolateElement } from '@wordpress/element';
import {
	Button,
	CardBody,
	Flex,
	FlexBlock,
	FlexItem,
} from '@wordpress/components';

/**
 * Internal dependencies.
 */
import { formatExplicitCurrency } from 'multi-currency/interface/functions';
import Loadable from 'components/loadable';
import OverviewCard from 'wcpay/components/overview-card';
import { useActiveLoanSummary } from 'wcpay/data/capital';
import { getAdminUrl } from 'wcpay/utils';

import './style.scss';
import { formatDateTimeFromTimestamp } from 'wcpay/utils/date-time';

const Block = ( {
	title,
	children,
}: {
	title: React.ReactNode;
	children: React.ReactNode;
} ): JSX.Element => (
	<FlexBlock className="wcpay-loan-summary-block">
		<div className="wcpay-loan-summary-block__title">{ title }</div>
		<div className="wcpay-loan-summary-block__value">{ children }</div>
	</FlexBlock>
);

const LoanSummaryLoadingState: React.FC = () => (
	<CardBody className="wcpay-loan-summary-body">
		<Flex align="normal" className="wcpay-loan-summary-row">
			<Block title={ __( 'Total repaid', 'woocommerce-payments' ) }>
				<Loadable
					isLoading={ true }
					aria-hidden
					display="inline"
					placeholder={ __(
						'Total repaid placeholder',
						'woocommerce-payments'
					) }
				/>
			</Block>
			<Block title={ __( 'Repaid this period', 'woocommerce-payments' ) }>
				<Loadable
					isLoading={ true }
					aria-hidden
					display="inline"
					placeholder={ __(
						'Repaid this period placeholder',
						'woocommerce-payments'
					) }
				/>
			</Block>
		</Flex>
		<Flex align="normal" className="wcpay-loan-summary-row is-bottom-row">
			<Block title={ __( 'Loan disbursed', 'woocommerce-payments' ) }>
				<Loadable
					isLoading={ true }
					aria-hidden
					display="inline"
					placeholder="Date disbursed"
				/>
			</Block>
			<Block title={ __( 'Loan amount', 'woocommerce-payments' ) }>
				<Loadable
					isLoading={ true }
					aria-hidden
					display="inline"
					placeholder="Loan amount"
				/>
			</Block>
			<Block title={ __( 'Fixed fee', 'woocommerce-payments' ) }>
				<Loadable
					isLoading={ true }
					aria-hidden
					display="inline"
					placeholder="Fixed fee"
				/>
			</Block>
			<Block title={ __( 'Withhold rate', 'woocommerce-payments' ) }>
				<Loadable
					isLoading={ true }
					aria-hidden
					display="inline"
					placeholder="Rate"
				/>
			</Block>
			<Block title={ __( 'First paydown', 'woocommerce-payments' ) }>
				<Loadable
					isLoading={ true }
					aria-hidden
					display="inline"
					placeholder={ __(
						'First paydown',
						'woocommerce-payments'
					) }
				/>
			</Block>
		</Flex>
	</CardBody>
);

const getActiveLoanId = () => {
	for ( const i in wcpaySettings.accountLoans.loans ) {
		const [ loanId, status ] =
			wcpaySettings.accountLoans.loans[ i ].split( '|' );
		if ( status === 'active' ) {
			return loanId;
		}
	}
	return null;
};

const ActiveLoanSummary = (): JSX.Element => {
	const { summary, isLoading } = useActiveLoanSummary();
	const showLoadingState = isLoading || ! summary;
	const activeLoanId = getActiveLoanId();

	const title = (
		<>
			<FlexItem>
				{ __( 'Active loan overview', 'woocommerce-payments' ) }
			</FlexItem>
			<FlexItem>
				{ ! showLoadingState && activeLoanId && (
					<Button
						variant="link"
						href={ getAdminUrl( {
							page: 'wc-admin',
							path: '/payments/transactions',
							type: 'charge',
							filter: 'advanced',
							loan_id_is: activeLoanId,
						} ) }
						__next40pxDefaultSize
					>
						{ __( 'View transactions', 'woocommerce-payments' ) }
					</Button>
				) }
			</FlexItem>
		</>
	);

	if ( showLoadingState ) {
		return (
			<OverviewCard
				title={ title }
				headerClassName="wcpay-loan-summary-header"
				isLoading={ true }
				LoadingState={ LoanSummaryLoadingState }
			/>
		);
	}

	const { details } = summary;

	return (
		<OverviewCard
			title={ title }
			headerClassName="wcpay-loan-summary-header"
			isLoading={ false }
			LoadingState={ LoanSummaryLoadingState }
		>
			<CardBody className="wcpay-loan-summary-body">
				<Flex align="normal" className="wcpay-loan-summary-row">
					<Block
						title={ __( 'Total repaid', 'woocommerce-payments' ) }
					>
						{ createInterpolateElement(
							sprintf(
								__(
									'<big>%s</big> of %s',
									'woocommerce-payments'
								),
								formatExplicitCurrency(
									details.paid_amount,
									details.currency
								),
								formatExplicitCurrency(
									details.fee_amount + details.advance_amount,
									details.currency
								)
							),
							{
								big: <span className="is-big" />,
							}
						) }
					</Block>
					<Block
						title={ sprintf(
							__(
								'Repaid this period (until %s)',
								'woocommerce-payments'
							),
							formatDateTimeFromTimestamp(
								details.current_repayment_interval.due_at
							)
						) }
					>
						{ createInterpolateElement(
							sprintf(
								__(
									'<big>%s</big> of %s minimum',
									'woocommerce-payments'
								),
								formatExplicitCurrency(
									details.current_repayment_interval
										.paid_amount,
									details.currency
								),
								formatExplicitCurrency(
									details.current_repayment_interval
										.paid_amount +
										details.current_repayment_interval
											.remaining_amount,
									details.currency
								)
							),
							{
								big: <span className="is-big" />,
							}
						) }
					</Block>
				</Flex>
				<Flex
					align="normal"
					className="wcpay-loan-summary-row is-bottom-row"
				>
					<Block
						title={ __( 'Loan disbursed', 'woocommerce-payments' ) }
					>
						{ formatDateTimeFromTimestamp(
							details.advance_paid_out_at
						) }
					</Block>
					<Block
						title={ __( 'Loan amount', 'woocommerce-payments' ) }
					>
						{ formatExplicitCurrency(
							details.advance_amount,
							details.currency
						) }
					</Block>
					<Block title={ __( 'Fixed fee', 'woocommerce-payments' ) }>
						{ formatExplicitCurrency(
							details.fee_amount,
							details.currency
						) }
					</Block>
					<Block
						title={ __( 'Withhold rate', 'woocommerce-payments' ) }
					>
						{ details.withhold_rate * 100 }%
					</Block>
					<Block
						title={ __( 'First paydown', 'woocommerce-payments' ) }
					>
						{ formatDateTimeFromTimestamp(
							details.repayments_begin_at
						) }
					</Block>
				</Flex>
			</CardBody>
		</OverviewCard>
	);
};

export default ActiveLoanSummary;
